import { CustomTypeOptions } from 'i18next'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef } from 'react'
import {
  AContainer,
  FixedMenuContainer,
  NavContainer
} from './map/FixedMenu.style'

interface MenuItem {
  label: string
  id: string
}

type EventTranslationKey = keyof Omit<
  CustomTypeOptions['resources']['event'],
  'mapbox'
>

interface FixedMenuProps {
  activeItem: string
  onItemClick: (item: string) => void
  hasInformation: boolean
  hasParcours: boolean
  hasLocation: boolean
  hasPracticalInformation: boolean
  hasRegistrationLists: boolean
  hasOrganizerInformation: boolean
  hasTimerInformation: boolean
}

const FixedMenu = ({
  activeItem,
  onItemClick,
  hasParcours,
  hasInformation,
  hasLocation,
  hasPracticalInformation,
  hasRegistrationLists,
  hasOrganizerInformation,
  hasTimerInformation
}: FixedMenuProps) => {
  const { t } = useTranslation('event')

  const createMenuItem = (
    condition: boolean,
    labelKey: EventTranslationKey,
    idKey: EventTranslationKey
  ): MenuItem[] => (condition ? [{ label: t(labelKey), id: t(idKey) }] : [])

  const menuItems: MenuItem[] = [
    { label: t('apropos'), id: t('aboutAnchor') },
    { label: t('run'), id: t('raceAnchor') },
    ...createMenuItem(
      hasRegistrationLists,
      'registrationList',
      'registrationListAnchor'
    ),
    ...createMenuItem(hasParcours, 'parcours', 'parcoursAnchor'),
    ...createMenuItem(hasLocation, 'location', 'locationAnchor'),
    ...createMenuItem(hasInformation, 'informations', 'informationAnchor'),
    ...createMenuItem(
      hasPracticalInformation,
      'practicalInformation',
      'practicalInformationAnchor'
    ),
    ...createMenuItem(hasOrganizerInformation, 'organizer', 'organizerAnchor'),
    ...createMenuItem(hasTimerInformation, 'timer', 'timerAnchor')
  ]

  const handleClick = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const navRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      menuItems.forEach((item) => {
        const element = document.getElementById(item.id)

        if (element && element.getBoundingClientRect().top < 100) {
          onItemClick(item.label)
        }
      })
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [menuItems, activeItem])

  useEffect(() => {
    const handleResize = () => {
      if (navRef.current) {
        const navWidth = navRef.current.scrollWidth
        const windowWidth = window.innerWidth
        navRef.current.style.width =
          navWidth <= windowWidth ? '100%' : 'fit-content'
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <FixedMenuContainer ref={navRef}>
      <NavContainer>
        {menuItems.map(({ label, id }) => (
          <AContainer
            key={label}
            href={`#${id}`}
            $isActive={activeItem === label}
            onClick={(e) => {
              e.preventDefault()
              handleClick(id)
            }}
          >
            {label}
          </AContainer>
        ))}
      </NavContainer>
    </FixedMenuContainer>
  )
}

export default FixedMenu
